<template>
	<tr class="hover:bg-gray-300 cursor-pointer" @click="redirectToTarget">
		<td class="px-6 py-4 whitespace-no-wrap w-128">
			<div class="flex items-center">
				<div class="mr-4 w-8 flex justify-center">
					<font-awesome-icon :icon="['fas', 'user']" size="2x" />
				</div>

				<div class="ml-4 text-left">
					<div class="text-md leading-5 font-medium text-gray-900 font-bold">{{ getName }}</div>
					<div class="text-md leading-5 text-gray-500">{{ getGroupName }}</div>
				</div>
			</div>
		</td>

		<td class="px-6 py-4 text-left whitespace-no-wrap">
			<div class="text-md leading-5 text-gray-900">{{ getCompanyName }}</div>
			<div class="text-md leading-5 text-gray-500">{{ getCompanyRelationshipType }}</div>
		</td>

		<td class="px-6 py-4 text-left whitespace-no-wrap" v-if="showTime">
			<div class="text-md leading-5 text-gray-900">{{ getDateString }}</div>
			<div class="text-md leading-5 text-gray-500">{{ getTimeString }}</div>
		</td>

		<td class="px-6 py-4 text-left whitespace-no-wrap" v-if="showAutor">
			<div class="text-md leading-5 text-gray-900">{{ getAuthorLevel }}</div>
			<div class="text-md leading-5 text-gray-500">{{ getAuthorName }}</div>
		</td>

		<td class="px-6 py-4 text-left whitespace-no-wrap">
			<div class="text-md leading-5 text-gray-900">{{ getLastLoginDateString }}</div>
			<div class="text-md leading-5 text-gray-500">{{ getLastLoginTimeString }}</div>
		</td>

		<td class="px-6 py-4 text-left whitespace-no-wrap">
			<div class="text-md leading-5 text-gray-900">{{ employee.count_logins }}</div>
		</td>

		<td class="px-6 py-4 text-left whitespace-no-wrap" v-if="showEmployeeIsEnabled">
			<div class="text-md leading-5 text-gray-900">
				<font-awesome-icon v-if="employee.is_enabled" :icon="['fas', 'check']" class="mr-4" />
			</div>
		</td>

		<td class="px-6 py-4 whitespace-no-wrap text-right text-lg font-medium">
			<font-awesome-icon
				:icon="['fas', employee.is_enabled ? 'user-slash' : 'user']"
				:class="{ 'mr-4': !employee.is_enabled, 'mr-3': employee.is_enabled }"
				class="cursor-pointer"
				@click.stop="disableEmployee"
				:title="employee.is_enabled ? $t('deactivate').toString() : $t('activate').toString()"
				v-if="getUser.id !== employee.id"
			/>
			<font-awesome-icon :icon="['fas', 'chevron-right']" class="cursor-pointer" />
		</td>
	</tr>
</template>
<script>
import { concatArray, getStringFromDate, getTimeFromDate } from '@/plugins/Utils';
import { AuthenticationApiFactory, CustomerconsultantsApiFactory, UserApiFactory } from '@/api/generated';
import apiDefaults from '@/api/defaultConfiguration';
import Authentication from '@/mixins/Authentication';

export default {
	name: 'EmployeeRow',
	mixins: [Authentication],
	props: {
		employee: {},
		showEmployeeIsEnabled: { default: false },
		showAutor: { default: true },
		showTime: { default: true },
	},
	data() {
		return {
			customerConsultantInput: {},
		};
	},
	methods: {
		redirectToTarget($event, url, params) {
			this.$router.push({ name: 'OtherUserprofile', params: { id: this.employee.id.toString() } });
		},
		disableEmployee() {
			CustomerconsultantsApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.customerconsultantsGet(this.employee.id)
				.then(response => {
					response.data.map((consultant, index) => {
						this.customerConsultantInput[consultant.personable_id] = consultant.firstName + ' ' + consultant.lastName;
					});
				});

			this.$swal({
				title: this.$t('sure').toString(),
				text: this.employee.is_enabled ? this.$t('deactivate_user').toString() : this.$t('activate_user').toString(),
				icon: 'warning',
				cancelButtonText: this.$t('general.cancel').toString(),
				showCancelButton: true,
			}).then(result => {
				if (result.value) {
					if (this.employee.is_enabled) {
						if (this.isUserAllowedInLevel5or6) {
							this.$swal({
								title: this.$t('sure').toString(),
								text: this.$t('transfer_customers_to_which_consultant').toString(),
								icon: 'warning',
								input: 'select',
								inputOptions: this.customerConsultantInput,
								cancelButtonText: this.$t('general.cancel').toString(),
								showCancelButton: true,
							}).then(reassignResult => {
								if (reassignResult.isConfirmed) {
									CustomerconsultantsApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
										.customerconsultantsIdReassigncustomersPut(this.employee.id, { newConsultant: reassignResult.value })
										.then(response => {
											this.employee.is_enabled = !this.employee.is_enabled;
											AuthenticationApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
												.userIdPut(this.employee.id, this.employee)
												.then(response => {
													this.$swal(this.$t('general.success').toString(), this.$t('user_deactivated').toString(), 'success');
												});
										})
										.catch(e => {
											this.$swal(this.$t('general.error').toString(), this.$t('customers_not_transferred').toString(), 'error');
										});
								}
							});
						} else {
							this.employee.is_enabled = !this.employee.is_enabled;
							AuthenticationApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
								.userIdPut(this.employee.id, this.employee)
								.then(response => {
									this.$swal(this.$t('general.success').toString(), this.$t('user_deactivated').toString(), 'success');
								});
						}
					} else {
						this.employee.is_enabled = !this.employee.is_enabled;
						AuthenticationApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
							.userIdPut(this.employee.id, this.employee)
							.then(response => {
								this.$swal(this.$t('general.success').toString(), this.$t('user_activated').toString(), 'success');
							});
					}
				}
			});
		},
	},
	computed: {
		getLastLoginDateString() {
			return this.getLastLoginDate ? getStringFromDate(this.getLastLoginDate) : '-';
		},
		getLastLoginTimeString() {
			return this.getLastLoginDate ? getTimeFromDate(this.getLastLoginDate) : '-';
		},
		getName() {
			return concatArray([this.employee.person.firstName, this.employee.person.lastName]);
		},
		getGroupName() {
			return this.employee?.usergroup?.name;
		},
		getAuthorName() {
			return concatArray([this.employee.created_by?.person?.firstName, this.employee.created_by?.person?.lastName]);
		},
		getAuthorLevel() {
			return this.employee.created_by?.usergroup?.name;
		},
		getLastLoginDate() {
			return this.employee?.last_login?.created_at ? new Date(this.employee.last_login.created_at * 1000) : null;
		},
		getDate() {
			return new Date(this.employee.created_at * 1000);
		},
		getDateString() {
			return getStringFromDate(this.getDate);
		},
		getTimeString() {
			return getTimeFromDate(this.getDate);
		},
		getCompanyName() {
			return concatArray([this.employee.person.company.name]);
		},
		getCompanyRelationshipType() {
			switch (this.employee.person.company.company_relationship_type) {
				case 'parent':
					return 'Hauptsitz';
				case 'child':
					return 'Niederlassung';
				default:
					return 'Einzelunternehmen';
			}
		},
	},
};
</script>
